import React, { useEffect } from "react";
import MaskedInput from "react-text-mask";
import { useField } from "formik";
import * as yup from "yup";

import OutlinedInput from "@mui/material/OutlinedInput";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";

import { Control } from "pages/eligibility/type";
import { keyFromIdentity } from "../helpers";
import { QuestionProps } from "../question";
import type { InputBaseComponentProps } from "@mui/material/InputBase";

export const validator = (
  question: QuestionProps,
  _questionsMap: Record<string, QuestionProps>,
  parent?: string,
  index?: number,
): Record<string, yup.AnySchema> => {
  const key = keyFromIdentity(question.identity, parent, index);
  return {
    [key]: yup.string().nullable().validSSN("Must be a valid 9 digit social security number"),
  };
};
const TextMaskCustom = React.forwardRef<HTMLInputElement, InputBaseComponentProps>(
  (props, ref) => {
    const { ...other } = props;
    const SSN_MASK = [/\d/, /\d/, /\d/, "-", /\d/, /\d/, "-", /\d/, /\d/, /\d/, /\d/];

    return (
      <MaskedInput
        {...other}
        ref={(instance: unknown) => {
          const masked = instance as { inputElement: HTMLElement | null } | null;
          const inputEl = masked ? (masked.inputElement as HTMLInputElement | null) : null;
          if (typeof ref === "function") {
            ref(inputEl);
          } else if (ref && typeof ref === "object") {
            (ref as React.MutableRefObject<HTMLInputElement | null>).current = inputEl;
          }
        }}
        mask={SSN_MASK}
        placeholderChar={"\u2000"}
      />
    );
  },
);

TextMaskCustom.displayName = "TextMaskCustom";

type FormQuestionSSNProps = { name: string; title: string; required?: boolean };
const FormQuestionSSN: Control<"FormQuestionText"> & { validator: typeof validator } = (props: unknown): React.ReactElement => {
  const { name, title, required } = props as FormQuestionSSNProps;
  const [field, meta, helpers] = useField(name);
  // Validate on transition:
  useEffect(() => () => {
    helpers.setTouched(true);
  }, []);

  return (
    <FormControl
      fullWidth
      variant="outlined"
      margin="dense"
    >
      <InputLabel htmlFor={`${name}-outlined`}>
        {title}
      </InputLabel>
      <OutlinedInput
        {...field}
        label={title}
        margin="dense"
        id={`${name}-outlined`}
        inputComponent={TextMaskCustom as React.ElementType<InputBaseComponentProps>}
        placeholder="xxx-xx-xxxx"
        required={required}
      />

      {meta.touched && meta.error && (
        <FormHelperText className="color-hc-error">
          {meta.error}
        </FormHelperText>
      )}
    </FormControl>
  );
};

FormQuestionSSN.validator = validator;
export default FormQuestionSSN;


